import { useAuthenticationContext } from "@climatepartner/app-shell/authentication-context";
import { Button } from "@climatepartner/components/Button";
import { renderVariable } from "@climatepartner/utils";
import { Alert, Box, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";

import { NETWORK_PATH } from "../../../../../shared/routing/paths/network";
import { ACCOUNT_CONNECTED_TOAST_KEY } from "../../../shared/account-connected-toast-storage";
import { useCommitAccountSelection } from "../api/use-commit-account-selection";
import { BUYER_NAME_FALLBACK } from "./invitation-header-card";

const cardSx = {
  borderRadius: 2,
  bgcolor: "background.paper",
  width: "100%",
} as const;

// Mirrors the `rootAccounts` entries on AccountSelectionScreenDto (resolve-invite-response.dto.ts).
export type RootAccountOption = {
  id: string;
  name: string;
};

type AccountSelectionCardProps = {
  connectionRequestId: string;
  rootAccounts: RootAccountOption[];
  buyerName: string | null;
};

export const AccountSelectionCard = ({
  connectionRequestId,
  rootAccounts,
  buyerName,
}: AccountSelectionCardProps): ReactElement => {
  const navigate = useNavigate();
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const { refreshToken } = useAuthenticationContext().actions;

  const commit = useCommitAccountSelection(connectionRequestId);

  const onContinue = (): void => {
    const selectedAccountName = rootAccounts.find((account) => account.id === selectedAccountId)?.name ?? "";

    commit.mutate(
      { accountId: selectedAccountId },
      // Per AIM-2632 (AC-7): land on the Network home in the current active tenant, intentionally
      // without switchToAccount.
      // The toast payload goes via sessionStorage: the AppShell's target_account redirect wipes
      // router state, so it can't ride navigate() here.
      {
        onSuccess: () => {
          sessionStorage.setItem(
            ACCOUNT_CONNECTED_TOAST_KEY,
            JSON.stringify({ buyerName: buyerName ?? BUYER_NAME_FALLBACK, selectedAccountName })
          );
          // The commit enabled network-supplier-v2 server-side; Frontegg's entitlements store
          // predates that grant and /network's guard reads it (AIM-2741).
          void refreshToken()
            .catch(() => undefined)
            .finally(() => navigate(NETWORK_PATH));
        },
      }
    );
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 560, width: "100%", mx: "auto" }}>
      <Alert severity="info" sx={{ borderRadius: 1 }}>
        You are a user in multiple accounts, select the right one to fulfil your buyer&apos;s request.
      </Alert>

      <Box sx={{ ...cardSx, px: 2.5, py: 3 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          Select an account
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 2 }}>
          Use your ClimatePartner credentials
        </Typography>

        <Stack spacing={2}>
          <FormControl size="small" variant="outlined" fullWidth>
            <InputLabel id="company-account-label">Company Account</InputLabel>
            <Select
              labelId="company-account-label"
              label="Company Account"
              value={selectedAccountId}
              onChange={(event) => setSelectedAccountId(event.target.value)}
            >
              {rootAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {renderVariable(account.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {commit.isError && (
            <Typography color="error" variant="body2">
              Something went wrong while confirming your account. Please try again.
            </Typography>
          )}

          <Button
            onClick={onContinue}
            fullWidth
            disabled={selectedAccountId === "" || commit.isPending}
            isLoading={commit.isPending}
          >
            Continue →
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};
